import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import "@/index.css";
import LandingPage from "@/components/landing/LandingPage";
import DemoExperience from "@/components/demo/DemoExperience";
import AuthPage from "@/components/auth/AuthPage";
import Dashboard from "@/components/builder/Dashboard";
import BuilderPage from "@/components/builder/BuilderPage";
import AdminDashboard from "@/components/admin/AdminDashboard";
import SettingsPage from "@/components/settings/SettingsPage";
import HelpPage from "@/components/HelpPage";
import NotFoundPage from "@/components/NotFoundPage";
import PrivacyPage from "@/components/PrivacyPage";
import { BlogList, BlogPost } from "@/components/landing/Blog";
import { useParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const LandingWithDemo = ({ user }) => {
  const [demo, setDemo] = useState(false);
  const nav = useNavigate();

  const onComplete = useCallback(async (demoData) => {
    setDemo(false);
    if (user && demoData?.html) {
      // Logged in — save site directly and open builder
      try {
        const siteRes = await axios.post(`${API}/sites`, {
          business_name: demoData.business_name || 'My Studio',
          business_type: demoData.business_type || 'photographer',
          style: demoData.style || 'romantic',
          notes: demoData.notes || '',
          html: demoData.html,
        }, { withCredentials: true });
        nav(`/builder/${siteRes.data.site.id}`);
      } catch (err) {
        console.error('Failed to save site:', err?.response?.data || err);
        // Don't navigate away — keep the demo open so user doesn't lose work
        setDemo(true);
      }
    } else {
      // Not logged in — send to auth with demo data
      nav('/auth', { state: { demoData } });
    }
  }, [nav, user]);

  return (
    <>
      <LandingPage onBuild={() => setDemo(true)} />
      <DemoExperience isOpen={demo} onClose={() => setDemo(false)} onSignup={onComplete} isLoggedIn={!!user} />
    </>
  );
};

const BuildFlow = ({ user }) => {
  const nav = useNavigate();
  const [demoOpen, setDemoOpen] = useState(true);
  const onComplete = useCallback(async (demoData) => {
    if (!demoData?.html) return;
    try {
      const siteRes = await axios.post(`${API}/sites`, {
        business_name: demoData.business_name || 'My Studio',
        business_type: demoData.business_type || 'photographer',
        style: demoData.style || 'romantic',
        notes: demoData.notes || '',
        html: demoData.html,
      }, { withCredentials: true });
      nav(`/builder/${siteRes.data.site.id}`);
    } catch (err) {
      console.error('Failed to save site:', err?.response?.data || err);
      // Keep demo open so user doesn't lose their generated site
      setDemoOpen(true);
    }
  }, [nav]);

  if (!user) { nav('/auth'); return null; }

  return (
    <div className="app-page">
      <DemoExperience isOpen={demoOpen} onSignup={onComplete} onClose={() => nav('/dashboard')} isLoggedIn={true} />
    </div>
  );
};

const BlogPostWrapper = () => { const { slug } = useParams(); return <BlogPost slug={slug} />; };

function App() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  // SEO: Set noindex for app pages, index for marketing pages
  useEffect(() => {
    const updateMeta = () => {
      const path = window.location.pathname;
      const noIndexPaths = ['/auth', '/dashboard', '/builder', '/settings', '/admin', '/build'];
      const shouldNoIndex = noIndexPaths.some(p => path.startsWith(p));
      let tag = document.querySelector('meta[name="robots"]');
      if (shouldNoIndex) {
        if (!tag) { tag = document.createElement('meta'); tag.name = 'robots'; document.head.appendChild(tag); }
        tag.content = 'noindex, nofollow';
      } else if (tag) {
        tag.remove();
      }
    };
    updateMeta();
    window.addEventListener('popstate', updateMeta);
    return () => window.removeEventListener('popstate', updateMeta);
  }, []);
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true }).then(r => setUser(r.data.user)).catch(() => {}).finally(() => setReady(true));
  }, []);

  // Only show loading screen for authenticated routes — marketing pages render instantly
  const isPublicRoute = typeof window !== 'undefined' && ['/', '/auth', '/help', '/blog'].some(p =>
    window.location.pathname === p || window.location.pathname.startsWith('/blog/')
  );

  if (!ready && !isPublicRoute) return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#faf8f5', fontFamily: 'var(--sans)' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201,168,76,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', animation: 'pulse 2s ease-in-out infinite' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
      </div>
      <p style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: '#1F1D1B', marginBottom: '6px', letterSpacing: '-.02em' }}>golden<em style={{ fontStyle: 'italic', color: '#C9A84C' }}>hour</em></p>
      <p style={{ fontSize: '13px', color: '#8c8782' }}>Loading your workspace...</p>
      <style>{`@keyframes pulse{0%,100%{opacity:0.4;transform:scale(0.95)}50%{opacity:1;transform:scale(1.05)}}`}</style>
    </div>
  );
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<div className="page-transition"><LandingWithDemo user={user} /></div>} />
        <Route path="/build" element={<div className="page-transition"><BuildFlow user={user} /></div>} />
        <Route path="/auth" element={<div className="page-transition"><AuthPage onAuth={setUser} /></div>} />
        <Route path="/dashboard" element={<div className="page-transition"><Dashboard user={user} /></div>} />
        <Route path="/builder/:siteId" element={<BuilderPage user={user} />} />
        <Route path="/settings" element={<div className="page-transition"><SettingsPage user={user} onAuth={setUser} /></div>} />
        <Route path="/admin" element={<div className="page-transition"><AdminDashboard user={user} /></div>} />
        <Route path="/help" element={<div className="page-transition"><HelpPage /></div>} />
        <Route path="/blog" element={<div className="page-transition"><BlogList /></div>} />
        <Route path="/blog/:slug" element={<div className="page-transition"><BlogPostWrapper /></div>} />
        <Route path="/privacy" element={<div className="page-transition"><PrivacyPage /></div>} />
        <Route path="*" element={<div className="page-transition"><NotFoundPage /></div>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
